/**
 * API 工具函数
 * 参考 tv-linux 项目的 util.js
 */

import { useAppStore } from '@/stores/appStore';

/**
 * 获取 App ID
 * 根据域名返回对应的 videoAppid
 */
export function getAppid(): { videoAppid: string; aegisAppid?: string } {
  const domain = typeof window !== 'undefined' ? window.location.hostname : '';
  const tmp: { videoAppid: string; aegisAppid?: string } = { videoAppid: '1200012' };

  switch (domain) {
    case 'tv.iflix.com':
      tmp.videoAppid = '1200016';
      tmp.aegisAppid = '6o4pohlPLD4jJJQx7z';
      break;
    case 'test.iflix.com':
      tmp.videoAppid = '1200016';
      tmp.aegisAppid = 'Jjloqil4L1ygV1yz5q';
      break;
    case 'static.wetv.vip':
    case 'tv.wetv.vip':
      tmp.videoAppid = '1200012';
      tmp.aegisAppid = 'PJg2Wf1x5OKoXgZKlD';
      break;
    case 'teststatic.wetv.vip':
    case 'testlinux.wetv.vip':
      tmp.videoAppid = '1200012';
      tmp.aegisAppid = 'Dv080FEw1RkJ0mjbwa';
      break;
    default:
      // 默认使用 WeTV
      tmp.videoAppid = '1200012';
      break;
  }

  return tmp;
}

/**
 * 获取 Cookie 值（简化版）
 */
export function getCookieAmend(key: string): string {
  if (typeof document === 'undefined') return '';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [k, v] = cookie.trim().split('=');
    if (k === key) {
      return decodeURIComponent(v || '');
    }
  }
  return '';
}

/**
 * 获取 Cookie 域名
 * 对标 tv-linux 项目 src/util/cookie.js
 * 本地开发环境不设置 domain，避免 cookie 读取问题
 */
function getCookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const { hostname } = window.location;
  if (hostname === 'tv.iflix.com' || hostname === 'test.iflix.com') {
    return '.iflix.com';
  }
  if (hostname.endsWith('.wetv.vip')) {
    return '.wetv.vip';
  }
  // 本地开发环境（localhost 等）不设置 domain
  return '';
}

/**
 * 设置 Cookie 值
 * 对标 tv-linux 项目 src/util/cookie.js 的 setCookieAmend
 * @param key Cookie 键名
 * @param value Cookie 值
 * @param isDoNotTrans 是否不进行转义（true: 不转义，false: 使用 escape 转义）
 */
export function setCookieAmend(key: string, value: string, isDoNotTrans?: boolean): void {
  if (typeof document === 'undefined') return;
  // 设置过期原则
  const DAYS = 3650; // 默认保留10年
  const exp = new Date(); // 计算过期时间
  exp.setTime(exp.getTime() + DAYS * 24 * 60 * 60 * 1000);

  // 使用 getCookieDomain() 统一获取 cookie 域名，替代已废弃的 document.domain
  // document.domain 已被标准弃用（https://developer.mozilla.org/docs/Web/API/Document/domain），
  // 且其值并不总是等于 window.location.hostname（尤其在跨域场景下可能抛出异常）
  const domain = getCookieDomain();
  const domainPart = domain ? `;domain=${domain}` : '';
  const encodedValue = isDoNotTrans ? value : escape(value);
  document.cookie = `${key}=${encodedValue};expires=${exp.toString()}${domainPart};path=/`;
}

/**
 * 生成 GUID（单个段）
 * 对标 tv-linux 项目 src/util/util.js 的 genGuid 函数
 * 生成 4 位十六进制字符串（如 "A1B2"）
 */
function genGuid(): string {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .padStart(5, '0') // 确保至少5位，不足则前面补0
    .substring(1) // 去掉第一位，保证是4位
    .toUpperCase();
}

/**
 * 设置 GUID（如果不存在）
 * 在初始化时调用，确保 guid 已生成
 * 对标 tv-linux: guid 由 8 个 4 位十六进制段组成（共 32 位）
 */
export function setGuid(): void {
  const existingGuid = getCookieAmend('guid');

  // 如果 guid 不存在或长度不是32位，重新生成
  if (!existingGuid || existingGuid?.length !== 32) {
    // 生成新的 GUID（大写）
    const guid = [genGuid(), genGuid(), genGuid(), genGuid(), genGuid(), genGuid(), genGuid(), genGuid()]
      .join('')
      .toUpperCase(); // 确保 GUID 为大写
    setCookieAmend('guid', guid);
  } else if (existingGuid !== existingGuid.toUpperCase()) {
    // 如果现有 GUID 不是大写，则转为大写后重新设置
    setCookieAmend('guid', existingGuid.toUpperCase());
  }
}

/**
 * 获取 Q-UA 参数
 * 参考 tv-linux 项目的 qua() 函数
 */
export function qua(): string {
  setGuid();
  // 从 appStore 获取渠道号
  const channelId = typeof window !== 'undefined' ? useAppStore.getState().vChannelId || '' : '';
  const deviceId = typeof window !== 'undefined' ? localStorage.getItem('deviceId') || '' : '';
  const langCode = getCookieAmend('lang_code') || 'zh-CN';
  const countryCode = getCookieAmend('country_code') || '';
  const videoAppid = getCookieAmend('video_appid') || getAppid().videoAppid;
  const guid = (getCookieAmend('guid') || '').toUpperCase(); // 转换为大写

  // 简化版 Q-UA，实际项目中可能需要更多参数
  return `QV=1&PR=VIDEO&PT=WETVLINUX&CHID=${channelId}&ORIPT=WETVLINUX&ORICHID=${channelId}&SV=10&MD=${deviceId}&TVKPlatform=WETVLINUX&LANG=${langCode}&DIST=${countryCode}&APPID=${videoAppid}&GUID=${guid}&licence=&timeforhj=${Date.now()}&video_appid=${videoAppid}`;
}

/**
 * 获取 URL 查询参数
 * 参考 tv-linux 项目的 getQueryVariable() 函数
 * @param variable 参数名
 * @returns 参数值，如果不存在则返回空字符串
 */
export function getQueryVariable(variable: string): string {
  if (typeof window === 'undefined') return '';
  let str = window.location.search; // 取得整个地址栏
  str = str.includes('?') ? str : window.location.href;
  str = decodeURIComponent(str);
  const num = str.indexOf('?');
  str = str.substring(num + 1); // 取得所有参数
  const arr = str.split('&'); // 各个参数放到数组里
  for (const item of arr) {
    const pair = item.split('=');
    if (pair[0] === variable) {
      return pair[1] || '';
    }
  }
  return '';
}

/**
 * 请求前处理（简化版）
 * 参考 tv-linux 项目的 beforeRequest() 函数
 */
export function beforeRequest(): void {
  setGuid();
  // 其他初始化逻辑可以在这里添加
  // 例如：设置 vplatform、video_appid、lang_code 等
}

/**
 * 清除所有 Cookie（除了白名单）
 * 参考 tv-linux 项目的 clearAllCookie() 函数
 * 在登录态失效时以及退出登录时会执行，清除用户的信息以及状态
 * guid信息和countryCode信息不用清除
 */
export function clearAllCookie(): void {
  if (typeof document === 'undefined') return;
  const keys = document.cookie.match(/[^ =;]+(?==)/g);
  // guid信息、country_code信息和语言信息不用清除
  // guid: 设备标识
  // country_code: 国家代码（开通会员需要）
  // wetv_lang: 语言代码（保留用户语言设置）
  // lang_code: 语言数字编码（保留用户语言设置）
  // i18nextLng: i18next 语言设置（保留用户语言设置）
  const whiteArr = ['guid', 'country_code', 'wetv_lang', 'lang_code', 'i18nextLng'];
  const domain = getCookieDomain();

  if (keys) {
    for (const key of keys) {
      if (!whiteArr.includes(key)) {
        const domainPart = domain ? `;domain=${domain}` : '';
        document.cookie = `${key}=0;expires=${new Date(0).toUTCString()}${domainPart};path=/`;
      }
    }
  }
}

/**
 * 删除 localStorage 指定项
 * @param key localStorage key
 */
export function delLocalStorage(key: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(key);
}

/**
 * 语言代码映射表（key 统一小写）
 */
const LANG_CODE_MAP: Record<string, string> = {
  'zh-cn': '1491963',
  'zh-tw': '8229847',
  en: '1491988',
  th: '1491973',
  vi: '1491994',
  id: '1491937',
  hi: '35',
  ms: '40',
  ja: '8',
  ko: '9',
  ar: '54',
  pt: '12',
  es: '14',
};

/**
 * 设置语言 Cookie
 * 统一处理语言切换时的 Cookie 设置逻辑
 * @param langCode 语言代码（如 'zh-CN', 'en' 等，会自动转换为小写）
 */
export function setLanguageCookie(langCode: string): void {
  if (typeof document === 'undefined') return;

  const langCodeLower = langCode.toLowerCase();
  const langCodeId = LANG_CODE_MAP[langCodeLower] || '1491988';

  // 使用 setCookieAmend 设置新的 Cookie
  // 语言代码和 ID 都是纯字符串，不需要转义，使用 isDoNotTrans=true
  setCookieAmend('wetv_lang', langCodeLower, true);
  setCookieAmend('lang_code', langCodeId, true);
}
